import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
  resolveProductFromPath,
} from "../../constants/trialProduct";
import styles from "./PlansComponent.module.css";
import api from "../../services/api";
import { Button } from "../ui";
import {
  CTA_PLACEMENTS,
  DESTINATIONS,
  PLAN_TYPE,
  resolveContextFromPath,
  trackTrialCtaClick,
} from "../../analytics";
import { isSchedulingIncluded } from "../../utils/planCapabilities";

/**
 * Define qual plano recebe o destaque "Mais popular": o 2º plano PAGO do
 * período exibido. Isolado de propósito para a regra ser fácil de ajustar.
 *
 * @param {Array<{id: string, name: string, price: number}>} plans
 * @returns {string|null} id do plano em destaque, ou null se não houver 2 pagos.
 */
const getPopularPlanId = (plans) => {
  const paid = plans.filter(
    (p) =>
      Number(p.price) > 0 &&
      !["free", "grátis", "gratis", "teste"].some((t) =>
        p.name.toLowerCase().includes(t),
      ),
  );
  // Decisão de produto: o plano recomendado é o Profissional — o que inclui a
  // Central de Atendimento (CRM). Quando algum plano pago expõe `crmIncluded`,
  // ele leva o destaque; senão, cai no heurístico histórico (2º plano pago).
  const crmPlan = paid.find((p) => p?.details?.crmIncluded);
  if (crmPlan) return crmPlan.id;
  return paid.length >= 2 ? paid[1].id : null;
};

/**
 * Detecta o plano de teste grátis (o "FREE"): preço zero ou nome que indique
 * gratuidade/teste. Usado tanto para sinalizar o card quanto para o CTA.
 *
 * @param {{name: string, price: number}} plan
 * @returns {boolean}
 */
const isFreePlan = (plan) =>
  Number(plan.price) === 0 ||
  ["free", "grátis", "gratis", "teste"].some((t) =>
    plan.name.toLowerCase().includes(t),
  );

/**
 * `true` somente para o trial gratuito estrutural da vitrine.
 *
 * Não usa o nome: um plano pago chamado "Teste Pro" continua sendo pago para
 * fins de catálogo e analytics. As guardas evitam que `null` e `""`, que o
 * JavaScript converte para zero, entrem como trial.
 *
 * @param {{price?: number|string|null}} plan
 * @returns {boolean}
 */
const isZeroPriceTrial = (plan) =>
  plan.price != null && plan.price !== "" && Number(plan.price) === 0;

/**
 * Mantém cada vitrine pública dentro da família vendida pela rota.
 * Fora dessas rotas não filtra, preservando as grades genéricas e de gestão.
 *
 * ⚠️ O TRIAL NÃO É EXCEÇÃO. A primeira versão desta função abria a guarda para
 * qualquer plano de preço zero, partindo de um catálogo em que só o `FREE`
 * (COM agenda) era `isActive:true` — o `FREE_ATENDIMENTO` era plano de signup,
 * fora da vitrine. Em produção os DOIS trials estão ativos, e a exceção passou
 * a deixar os dois cards gratuitos lado a lado nas duas vitrines: o visitante
 * de `/atendimento` via um "Teste grátis" que cria conta COM agenda, e o de
 * `/agendamento` via um que cria conta SEM. Falha silenciosa da mesma família
 * do `?produto=` ausente — o cadastro responde 201 e só o bot denuncia.
 *
 * A regra da família vale para todo plano, pago ou grátis: quem separa é
 * `schedulingIncluded`, nunca o preço. `isZeroPriceTrial` continua existindo,
 * mas para a única pergunta que é de fato sobre preço — o gate do funil de
 * trial no analytics (ver `handlePlanChoice`).
 *
 * @param {{price?: number|string|null, schedulingIncluded?: boolean}} plan
 * @param {string|undefined} product
 * @returns {boolean}
 */
const belongsToPublicProduct = (plan, product) => {
  if (!product) return true;

  // Atendimento só aceita `false` EXPLÍCITO; Agendamento fica com `true` e com
  // o campo ausente — o fail-safe canônico da ADR-0026, que mantém plano de
  // família desconhecida na vitrine maior em vez de sumir com ele.
  return product === TRIAL_PRODUCTS.ATENDIMENTO
    ? !isSchedulingIncluded(plan)
    : isSchedulingIncluded(plan);
};

/**
 * Grade de planos.
 *
 * Tem DOIS modos, controlados por props (retrocompatível):
 *
 * 1. Modo VENDA (default — nenhuma prop nova): comportamento histórico usado na
 *    home de marketing e no fluxo de cadastro. Cada plano tem "Assinar Agora" e
 *    o clique salva a intenção em localStorage + navega para /registration.
 *
 * 2. Modo GESTÃO (quando `onManageCta` é passado): usado na tela de gestão de
 *    assinatura. O plano vigente (`currentPlanId`) é marcado como "Seu plano
 *    atual" (sem botão de assinar) e os demais exibem o CTA "Mudar para este
 *    plano", que delega ao callback `onManageCta(plan)`. O callback abre uma
 *    confirmação e chama a ação REAL de troca (ADR-0019) no componente pai —
 *    aqui NÃO há chamada de cobrança direta. Se `pendingPlanId` bater com um
 *    plano, ele é marcado como "Troca agendada" (sem CTA).
 *
 * @param {object}   [props]
 * @param {string|null} [props.currentPlanId] id do plano vigente (modo gestão).
 * @param {string|null} [props.pendingPlanId] id do plano com troca agendada.
 * @param {(plan: {id: string, name: string, price: number}) => void} [props.onManageCta]
 *   callback do CTA "trocar" (ativa o modo gestão quando presente).
 * @param {boolean} [props.manageDisabled] desabilita de VERDADE o CTA de troca
 *   de todos os cards. Só tem efeito no modo GESTÃO — o modo venda ignora.
 *   Existe porque o pai já podia deixar o `onManageCta` inerte (tenant sem
 *   cobrança self-service — ADR-0034 §7), e um botão que parece clicável e não
 *   responde lê como app quebrado. Desabilitar, e não esconder: sumir com o CTA
 *   faz o card parecer defeituoso; desabilitado + o aviso de modo que o pai
 *   renderiza acima da grade explicam o porquê. Não substitui o guard do pai —
 *   acompanha (defesa em profundidade).
 */
const PlansComponent = ({
  currentPlanId = null,
  pendingPlanId = null,
  onManageCta = null,
  manageDisabled = false,
} = {}) => {
  const manageMode = typeof onManageCta === "function";
  const [monthlyPlans, setMonthlyPlans] = useState([]);
  const [annualPlans, setAnnualPlans] = useState([]);
  const [semesterPlans, setSemesterPlans] = useState([]);
  const [period, setPeriod] = useState("/mês");

  const navigate = useNavigate();
  const location = useLocation();
  /**
   * Produto que o TESTE desta rota cria — a mesma fonte que decide o destino do
   * clique (ver `handlePlanChoice`). É de propósito: o que o card PROMETE e o
   * que o clique CRIA precisam sair do mesmo lugar, senão voltam a divergir.
   * `undefined` fora das rotas de produto (grade de gestão, por exemplo), onde
   * vale o default do backend — agendamento.
   */
  const trialProduct = resolveProductFromPath(location.pathname);
  const trialSemAgenda = trialProduct === TRIAL_PRODUCTS.ATENDIMENTO;

  const handlePlanChoice = (plan) => {
    // Definimos como "Free" se for teste de 14 dias, grátis ou valor 0
    const isFree = isFreePlan(plan);

    // Medição do funil: só preço ZERO conta como CTA de teste grátis.
    // Condição estrita de propósito — `isFreePlan` também casa por NOME
    // ("free"/"grátis"/"teste"), e um plano PAGO chamado "Teste Pro" entraria
    // no funil de trial por engano. A regra do CTA não é a regra do card.
    // As guardas contra `null`/`""` são deliberadas, não redundância:
    // `Number(null)` e `Number("")` valem 0, então um plano "sob consulta"
    // vindo da API com `price: null` seria classificado como teste grátis.
    if (isZeroPriceTrial(plan)) {
      trackTrialCtaClick({
        // Componente montado em rotas diferentes (home, cadastro): a página vem
        // da rota real, não existe uma página "plans".
        ...resolveContextFromPath(location.pathname),
        cta_placement: CTA_PLACEMENTS.PLANS,
        destination: DESTINATIONS.REGISTRATION,
        plan_type: PLAN_TYPE.FREE,
        product: trialProduct,
      });
    }

    // Salvar intenção de assinar no localStorage
    localStorage.setItem(
      "pendingSubscription",
      JSON.stringify({
        planId: plan.id,
        isFree: isFree,
      }),
    );

    // O card de TESTE precisa carregar o produto da rota. Este componente é
    // montado em `/agendamento#planos` e em `/atendimento#planos`: sem o
    // `?produto=`, quem clicasse em "Testar por 14 dias" lendo a página de
    // Atendimento cairia no default do backend — o trial de Agendamento, COM
    // agenda, contradizendo a página inteira. Silencioso, como sempre.
    //
    // Só o teste grátis leva produto: para plano PAGO o que decide é o
    // `pendingSubscription` acima, e o produto viria de graça no lugar errado.
    if (isFree) {
      navigate(buildTrialSignupPath(trialProduct));
      return;
    }

    // Redireciona para login/registro
    navigate("/registration");
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get("/plans");
        const plans = response.data;

        const formatPlan = (plan) => ({
          name: plan.name,
          price: plan.price,
          id: plan.id,
          period: plan.period,
          details: {
            aiIncluded: plan.aiIncluded,
            aiType: plan.aiType,
            crmIncluded: plan.crmIncluded,
            leadsLimit: plan.leadsLimit,
            membersLimit: plan.membersLimit,
            messagesLimit: plan.messagesLimit,
            reportsType: plan.reportsType,
            supportType: plan.supportType,
            // ADR-0026 — o Plano Atendimento não agenda. Fail-safe igual ao do
            // resto do app: só `false` explícito conta como "não inclui".
            schedulingIncluded: plan.schedulingIncluded !== false,
          },
        });

        // Variantes FUNDADOR (e planos legados BRONZE/PRATA/OURO) são
        // `isActive:false` no banco e `GET /plans` já filtra por `isActive:true`
        // — este filtro é só rede de segurança para o dia em que alguém tirar o
        // where do backend. Fail-safe: plano sem o campo continua visível.
        const sellable = plans
          .filter((plan) => plan.isActive !== false)
          .filter((plan) => belongsToPublicProduct(plan, trialProduct));

        // ORDEM = PREÇO CRESCENTE. A API devolve na ordem de inserção do seed, o
        // que colocaria o Atendimento (adicionado por último) DEPOIS do
        // Profissional. A escada comercial precisa ser lida de baixo para cima:
        // Atendimento R$ 87 → Básico R$ 147 → Profissional R$ 397. Ordenar por
        // preço mantém isso verdadeiro sem hardcodar nome de plano.
        const byPrice = (a, b) => Number(a.price) - Number(b.price);

        const ofPeriod = (period) =>
          sellable
            .filter((plan) => plan.period === period)
            .map(formatPlan)
            .sort(byPrice);

        const monthly = ofPeriod("MONTHLY");
        const annual = ofPeriod("ANNUAL");
        const semester = ofPeriod("SEMESTER");

        setMonthlyPlans(monthly);
        setAnnualPlans(annual);
        setSemesterPlans(semester);
      } catch (error) {
        // fetch falhou — mantém lista de planos vazia
      }
    };

    fetchPlans();
  }, [trialProduct]);

  const Card = ({ id, name, price, details, highlighted }) => {
    const isCurrent = manageMode && id === currentPlanId;
    const isPending = manageMode && !isCurrent && id === pendingPlanId;
    const free = isFreePlan({ name, price });
    return (
      <div
        className={[
          styles.card,
          highlighted ? styles.cardPopular : "",
          isCurrent ? styles.cardCurrent : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {isCurrent ? (
          <span className={styles.currentBadge}>Seu plano atual</span>
        ) : isPending ? (
          <span className={styles.currentBadge}>Troca agendada</span>
        ) : (
          highlighted && (
            <span className={styles.popularBadge}>Mais popular</span>
          )
        )}
        <div className={styles.cardTitle}>
          {free ? (
            <>
              <h5 className={styles.planName}>Teste grátis · 14 dias</h5>
              <span className={styles.planSubLabel}>
                {trialSemAgenda ? "atendimento, sem agenda" : "tudo liberado"}
              </span>
            </>
          ) : (
            <h5 className={styles.planName}>
              {name.replace(/\s(ANNUAL|ANUAL|SEMESTRAL|MONTHLY)/g, "")}
            </h5>
          )}
        </div>
        <div className={styles.cardPrice}>
          <h2 className={styles.planPrice}>
            R${" "}
            {parseFloat(price).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximimFractionDigits: 2,
            })}{" "}
          </h2>
        </div>

        <span className={styles.periodText}>
          {period === "/ano" ? "/ano" : period === "/mês" ? "/mês" : "/sem"}
        </span>
        {free && (
          <p className={styles.freeNote}>
            {trialSemAgenda
              ? "No teste você usa o chatbot de atendimento: respostas, preços e captação de contatos. A agenda não entra — ela é do Chatbot de Agendamento. Depois dos 14 dias, valem os limites do plano que você escolher."
              : "No teste você usa todos os recursos. Depois dos 14 dias, valem os limites do plano que você escolher."}
          </p>
        )}
        <div className={styles.cardBody}>
          {/* O que cada plano inclui (decisão de produto). Atendimento = ChatBot
              que responde e informa preços, SEM agenda; Básico = ChatBot +
              agendamento; Profissional = tudo do Básico + Central de Atendimento
              (CRM).

              No card de TESTE a lista é omitida e quem explica é o `freeNote` —
              que desde 18/08/2026 muda com a rota. Antes ele podia dizer "tudo
              liberado" sem ressalva porque havia UM trial; hoje há dois, e em
              `/atendimento` o teste criado é o FREE_ATENDIMENTO, sem agenda e
              sem CRM. Card que promete o que o cadastro não entrega é a mesma
              falha silenciosa do destino sem `?produto=`. */}
          {!free && (
            <ul
              className={styles.inclusions}
              aria-label="O que está incluído neste plano"
            >
              {details.schedulingIncluded ? (
                <li className={styles.inclusionItem}>
                  <span className={styles.inclusionCheck} aria-hidden="true">
                    ✓
                  </span>
                  <span>ChatBot com agendamento por IA</span>
                </li>
              ) : (
                <>
                  <li className={styles.inclusionItem}>
                    <span className={styles.inclusionCheck} aria-hidden="true">
                      ✓
                    </span>
                    <span>
                      ChatBot por IA: responde dúvidas e informa preços
                    </span>
                  </li>
                  <li
                    className={`${styles.inclusionItem} ${styles.inclusionLocked}`}
                  >
                    <span className={styles.inclusionLock} aria-hidden="true">
                      🔒
                    </span>
                    <span>Agendamento — a partir do Básico</span>
                  </li>
                </>
              )}
              {details.crmIncluded ? (
                <li
                  className={`${styles.inclusionItem} ${styles.inclusionHighlight}`}
                >
                  <span className={styles.inclusionCheck} aria-hidden="true">
                    ✓
                  </span>
                  <span>
                    Central de Atendimento: filas, setores, atribuição, tags e
                    notas
                  </span>
                </li>
              ) : (
                <li
                  className={`${styles.inclusionItem} ${styles.inclusionLocked}`}
                >
                  <span className={styles.inclusionLock} aria-hidden="true">
                    🔒
                  </span>
                  <span>Central de Atendimento — no Profissional</span>
                </li>
              )}
            </ul>
          )}
          <ul className={styles.featuresList}>
            <li>
              <div className={styles.listDivs}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M16 17v2H2v-2s0-4 7-4s7 4 7 4m-3.5-9.5A3.5 3.5 0 1 0 9 11a3.5 3.5 0 0 0 3.5-3.5m3.44 5.5A5.32 5.32 0 0 1 18 17v2h4v-2s0-3.63-6.06-4M15 4a3.4 3.4 0 0 0-1.93.59a5 5 0 0 1 0 5.82A3.4 3.4 0 0 0 15 11a3.5 3.5 0 0 0 0-7"
                  ></path>
                </svg>
                <span className={styles.label}>Membros</span>
                <span className={styles.value}>
                  {details.membersLimit === null
                    ? "ILIMITADOS"
                    : details.membersLimit}
                </span>
              </div>
            </li>
            <li>
              <div className={styles.listDivs}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M10 4c2.2 0 4 1.8 4 4s-1.8 4-4 4s-4-1.8-4-4s1.8-4 4-4m7 17l1.8 1.77c.5.5 1.2.1 1.2-.49V18l2.8-3.4A1 1 0 0 0 22 13h-7c-.8 0-1.3 1-.8 1.6L17 18zm-2-2.3l-2.3-2.8c-.4-.5-.6-1.1-.6-1.7c-.7-.2-1.4-.2-2.1-.2c-4.4 0-8 1.8-8 4v2h13z"
                  ></path>
                </svg>
                <span className={styles.label}>Leads</span>
                <span className={styles.value}>{details.leadsLimit}</span>
              </div>
            </li>
            <li>
              <div className={styles.listDivs}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M11 17V7H4v10zm0-14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm5.5 0h5A1.5 1.5 0 0 1 23 4.5v3A1.5 1.5 0 0 1 21.5 9H18l-3 3V4.5A1.5 1.5 0 0 1 16.5 3"
                  ></path>
                </svg>
                <span className={styles.label}>Mensagens</span>
                <span className={styles.value}>{details.messagesLimit}</span>
              </div>
            </li>
            <li>
              <div className={styles.listDivs}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M18.72 14.76c.35-.85.54-1.76.54-2.76c0-.72-.11-1.41-.3-2.05c-.65.15-1.33.23-2.04.23A9.07 9.07 0 0 1 9.5 6.34a9.2 9.2 0 0 1-4.73 4.88c-.04.25-.04.52-.04.78A7.27 7.27 0 0 0 12 19.27c1.05 0 2.06-.23 2.97-.64c.57 1.09.83 1.63.81 1.63c-1.64.55-2.91.82-3.78.82c-2.42 0-4.73-.95-6.43-2.66a9 9 0 0 1-2.24-3.69H2v-4.55h1.09a9.09 9.09 0 0 1 15.33-4.6a9 9 0 0 1 2.47 4.6H22v4.55h-.06L18.38 18l-5.3-.6v-1.67h4.83zm-9.45-2.99c.3 0 .59.12.8.34a1.136 1.136 0 0 1 0 1.6c-.21.21-.5.33-.8.33c-.63 0-1.14-.5-1.14-1.13s.51-1.14 1.14-1.14m5.45 0c.63 0 1.13.51 1.13 1.14s-.5 1.13-1.13 1.13s-1.14-.5-1.14-1.13a1.14 1.14 0 0 1 1.14-1.14"
                  ></path>
                </svg>
                <span className={styles.label}>Suporte</span>
                <span className={styles.value}>{details.supportType}</span>
              </div>
            </li>
            <li>
              <div className={styles.listDivs}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2M9 17H7v-7h2zm4 0h-2V7h2zm4 0h-2v-4h2z"
                  ></path>
                </svg>
                <span className={styles.label}>Relatórios</span>
                <span className={styles.value}>
                  {details.reportsType === "COMPLETE"
                    ? "COMPLETOS"
                    : details.reportsType === "SIMPLE"
                      ? "SIMPLES"
                      : "AVANÇADOS"}
                </span>
              </div>
            </li>
            {details.aiIncluded && (
              <li>
                <div className={styles.listDivs}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2M7.5 13A2.5 2.5 0 0 0 5 15.5A2.5 2.5 0 0 0 7.5 18a2.5 2.5 0 0 0 2.5-2.5A2.5 2.5 0 0 0 7.5 13m9 0a2.5 2.5 0 0 0-2.5 2.5a2.5 2.5 0 0 0 2.5 2.5a2.5 2.5 0 0 0 2.5-2.5a2.5 2.5 0 0 0-2.5-2.5"
                    ></path>
                  </svg>
                  <span className={styles.label}>IA</span>
                  <span className={styles.value}>
                    {details.aiType === "BASIC"
                      ? "BÁSICA"
                      : details.aiType === "ADVANCED"
                        ? "AVANÇADA"
                        : "PERSONALIZADA"}
                  </span>
                </div>
              </li>
            )}
          </ul>
        </div>
        {manageMode ? (
          // O plano vigente e o plano com troca agendada já são sinalizados pelo
          // `currentBadge` no topo do card; por isso ficam sem CTA no rodapé. Os
          // demais planos exibem o CTA de troca (ação REAL — ADR-0019 — disparada
          // pelo pai via `onManageCta`, sob confirmação).
          isCurrent ? null : isPending ? (
            // Mesmo estado visual do CTA travado por modo de cobrança: dois
            // botões desabilitados lado a lado na mesma grade não podem ter
            // aparências diferentes. Herdar só o `opacity: .7` do Button base
            // deixava este rótulo em 2,74:1 — reprova AA.
            <Button
              variant="secondary"
              className={`${styles.manageBtn} ${styles.manageBtnDisabled}`}
              disabled
              aria-disabled
            >
              Troca agendada
            </Button>
          ) : (
            <Button
              variant="secondary"
              className={[
                styles.manageBtn,
                manageDisabled ? styles.manageBtnDisabled : "",
              ]
                .filter(Boolean)
                .join(" ")}
              disabled={manageDisabled}
              // Redundante com o `disabled` nativo, e de propósito: leitores de
              // tela antigos e o modo de navegação por formulário do JAWS não
              // anunciam o `disabled` de forma consistente.
              aria-disabled={manageDisabled || undefined}
              onClick={() => onManageCta({ id, name, price })}
            >
              Mudar para este plano
            </Button>
          )
        ) : (
          <Button
            variant="primary"
            className={styles.subscribeBtn}
            onClick={() => handlePlanChoice({ id, name, price })}
          >
            {name === "FREE" ? "Testar por 14 dias" : "Assinar Agora"}
          </Button>
        )}
      </div>
    );
  };

  const displayedPlans =
    period === "/ano"
      ? annualPlans
      : period === "/mês"
        ? monthlyPlans
        : semesterPlans;

  const popularPlanId = getPopularPlanId(displayedPlans);

  return (
    <>
      <div className={styles.mainPlans}>
        <h2 className={styles.title}>Escolha o melhor plano para você</h2>
        <div className={styles.btns}>
          <Button
            variant={period === "/mês" ? "primary" : "ghost"}
            className={styles.toggleBtn}
            onClick={() => setPeriod("/mês")}
          >
            Mensal
          </Button>
          <Button
            variant={period === "/sem" ? "primary" : "ghost"}
            className={styles.toggleBtn}
            onClick={() => setPeriod("/sem")}
          >
            Semestral
          </Button>
          <Button
            variant={period === "/ano" ? "primary" : "ghost"}
            className={styles.toggleBtn}
            onClick={() => setPeriod("/ano")}
          >
            Anual
          </Button>
        </div>

        <div className={styles.cardsContainer}>
          {displayedPlans.map((plan) => (
            <Card
              key={plan.id}
              id={plan.id}
              name={plan.name}
              price={plan.price}
              details={plan.details}
              highlighted={plan.id === popularPlanId}
            />
          ))}
        </div>

        {trialProduct === TRIAL_PRODUCTS.ATENDIMENTO ? (
          <p className={styles.consultNote}>
            Precisa que o agente te ajude com agendamentos?{" "}
            <a
              className={styles.consultLink}
              href="/agendamento#planos"
              aria-label="Ver planos com agendamento"
            >
              Clica aqui
            </a>
            .
          </p>
        ) : trialProduct === TRIAL_PRODUCTS.AGENDAMENTO ? (
          <p className={styles.consultNote}>
            Não precisa que o agente te ajude com agendamentos, só quer que ele
            não te deixe perder leads?{" "}
            <a
              className={styles.consultLink}
              href="/atendimento#planos"
              aria-label="Ver planos de atendimento sem agendamento"
            >
              Clica aqui
            </a>
            .
          </p>
        ) : null}
      </div>
    </>
  );
};

export default PlansComponent;
